"use client";

import { Bot, Ear, FileCheck } from "lucide-react";
import { Card } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function TradingWorkflow() {
  const header = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const grid = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const steps = [{
    icon: Bot,
    title: "Submit Request",
    description: "Post your specific trade requirements on our secure platform, and our intelligent matching engine instantly alerts verified partners.",
    gradient: "from-blue-900 to-blue-700",
    offset: false
  }, {
    icon: Ear,
    title: "Smart Matching",
    description: "Our system analyzes global market data to connect you with the most reliable suppliers or buyers for your specific commodity.",
    gradient: "from-blue-800 to-blue-600",
    offset: true
  }, {
    icon: FileCheck,
    title: "Secure Execution",
    description: "Finalize your trade with confidence through our verified documentation process and secure transaction management tools.",
    gradient: "from-blue-700 to-blue-900",
    offset: false
  }];
  return <section data-section-id="18071" id="dualprocessworkflow" className="relative overflow-hidden bg-white text-gray-900 py-24 md:py-32">
      {}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 opacity-50">
        <div className="absolute top-1/4 left-1/4 h-[500px] w-[500px] rounded-lg bg-blue-100 blur-3xl opacity-40" />
        <div className="absolute bottom-1/4 right-1/4 h-[500px] w-[500px] rounded-lg bg-blue-200 blur-3xl opacity-40" />
      </div>

      <div className="relative mx-auto max-w-7xl px-5 md:px-10">
        {}
        <div ref={header.ref} className={`mx-auto max-w-3xl text-center transition-all duration-700 ease-out ${header.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <p className="font-default text-xs font-medium uppercase tracking-[0.14em] text-blue-900 mb-5">
            The Trading Process
          </p>
          <h2 className="font-default text-4xl md:text-5xl lg:text-[3.5rem] font-semibold leading-[1.04] tracking-tight text-gray-900">
            From inquiry to{" "}
            <span className="italic text-blue-900">transaction</span> in three
            steps.
          </h2>
          <p className="mt-6 text-lg md:text-xl text-gray-700 leading-relaxed">
            No complex negotiations, no hidden risks. KTEX streamlines global trade so your business can scale with confidence.
          </p>
        </div>

        {}
        <div ref={grid.ref} className={`relative mt-20 md:mt-24 transition-all duration-700 ease-out ${grid.isVisible ? "opacity-100" : "opacity-0"}`}>
          {}
          <svg aria-hidden="true" className="pointer-events-none absolute inset-x-0 top-[88px] hidden md:block w-full h-24" viewBox="0 0 1200 100" preserveAspectRatio="none" fill="none">
            <defs>
              <linearGradient id="flow-connector" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#1a3a52" stopOpacity="0.3" />
                <stop offset="50%" stopColor="#1a3a52" stopOpacity="0.3" />
                <stop offset="100%" stopColor="#1a3a52" stopOpacity="0.3" />
              </linearGradient>
            </defs>
            <path d="M 200 20 Q 450 90, 600 50 T 1000 20" stroke="url(#flow-connector)" strokeWidth="1" fill="none" />
          </svg>

          {}
          <div aria-hidden="true" className="pointer-events-none absolute left-[44px] top-16 bottom-16 w-px md:hidden" style={{
          background: "linear-gradient(to bottom, #1a3a52, #1a3a52, #1a3a52)"
        }} />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-6">
            {steps.map((step, i) => {
            const Icon = step.icon;
            return <div key={i} data-index={i} className={`relative transition-all duration-700 ease-out ${grid.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"} ${step.offset ? "md:mt-6" : ""}`} style={{
              transitionDelay: `${i * 120}ms`
            }}>
                  <Card className="group relative h-full border border-gray-200 bg-white backdrop-blur-xl rounded-lg p-7 md:p-9 shadow-sm transition-all duration-[350ms] ease-out hover:-translate-y-1 hover:shadow-md">
                    {}
                    <div className="flex items-center gap-4 mb-5">
                      <div className={`relative flex h-14 w-14 items-center justify-center rounded-lg bg-gradient-to-br ${step.gradient} opacity-90`}>
                        <Icon className="absolute h-6 w-6 text-white" strokeWidth={1.5} />
                      </div>
                      <span className="font-mono text-xs tracking-[0.04em] text-gray-500">
                        {`0${i + 1}`}
                      </span>
                    </div>

                    <h3 className="font-default text-2xl font-semibold tracking-tight text-gray-900 mb-3">
                      {step.title}
                    </h3>
                    <p className="text-base leading-relaxed text-gray-700">
                      {step.description}
                    </p>
                  </Card>
                </div>;
          })}
          </div>
        </div>
      </div>
    </section>;
}